import React, { useEffect, useRef } from 'react';
import './genExpense.css';
import { GooglePlayIcon, AppStoreIcon } from './GenExpFooterIcons';

// ─── Sub-components ──────────────────────────────────────────────────────────

const GenExpHeader = ({ title = 'Expense', invoiceNo }) => (
  <div className="genExp-header">
    <h1>{title}</h1>
    <div className="genExp-invoice-no">{invoiceNo}</div>
  </div>
);

const GenExpTopInfo = ({ firm, vendor, meta }) => (
  <div className="genExp-top-info">
    <div className="genExp-left">
      <div className="genExp-box">
        <p><strong>{firm?.name}</strong></p>
        <p>Address: {firm?.address}</p>
        <p><span>Contact:</span> {firm?.contact}</p>
      </div>
      <div className="genExp-box">
        <p><strong>{vendor?.name}</strong></p>
        <p>Address: {vendor?.address}</p>
        <p>
          <span>Contact:</span> {vendor?.contact} |{' '}
          <span>GSTIN: <strong>{vendor?.gstin}</strong></span>
        </p>
      </div>
    </div>

    <div className="genExp-right">
      <p><span>Date of Expense</span>{meta?.date}</p>
      <p><span>Bill Number</span>{meta?.billNumber}</p>
      <p><span>Place of Supply</span>{meta?.placeOfSupply}</p>
    </div>
  </div>
);

const GenExpTable = ({ items = [], totals = {} }) => (
  <table className="genExp-table">
    <thead>
      <tr>
        <th>#</th>
        <th>Description</th>
        <th>HSN</th>
        <th>Qty</th>
        <th>Rate</th>
        <th>Discount</th>
        <th>Tax(%)</th>
        <th>CGST</th>
        <th>SGST</th>
        <th>Total</th>
      </tr>
    </thead>
    <tbody>
      {items.map((item, i) => (
        <tr key={i}>
          <td>{i + 1}</td>
          <td>
            {item.description}<br />
            <small>{item.category}</small>
          </td>
          <td>{item.hsn}</td>
          <td>{item.qty}</td>
          <td>₹{item.rate}</td>
          <td>₹{item.discount}<br />{item.discountPercent}%</td>
          <td>₹{item.tax}</td>
          <td>₹{item.cgst}<br />{item.cgstPercent}%</td>
          <td>₹{item.sgst}<br />{item.sgstPercent}%</td>
          <td>₹{item.total}</td>
        </tr>
      ))}

      <tr className="genExp-total-row">
        <td colSpan={3}>Total (₹)</td>
        <td>{totals.qty}</td>
        <td>₹{totals.rate}</td>
        <td>₹{totals.discount}</td>
        <td>₹{totals.tax}</td>
        <td>₹{totals.cgst}</td>
        <td>₹{totals.sgst}</td>
        <td>₹{totals.total}</td>
      </tr>
    </tbody>
  </table>
);

const GenExpBottom = ({ amountInWords, termsConditions, summary, firmName }) => (
  <div className="genExp-bottom">
    <div className="genExp-left">
      <p className="genExp-words">
        <strong>Total Invoice Amount (in words)</strong>
        <span>{amountInWords}</span>
      </p>
      <p className="genExp-terms">{termsConditions}</p>
    </div>

    <div className="genExp-right genExp-summary">
      <div className="genExp-summary-details">
        <div className="genExp-amounts genExp-opacity-60">
          <span>Taxable Amount</span><span>₹{summary?.taxableAmount}</span>
        </div>
        <div className="genExp-amounts genExp-opacity-60">
          <span>Discount</span><span>₹{summary?.discount}</span>
        </div>
        <div className="genExp-amounts genExp-opacity-60">
          <span>Tax Amount</span><span>₹{summary?.taxAmount}</span>
        </div>
        <div className="genExp-amounts genExp-bold">
          <span>Total Invoice Amount</span><span>₹{summary?.totalInvoiceAmount}</span>
        </div>
        <div className="genExp-amounts">
          <span>Payment Received</span><span>₹{summary?.paymentReceived}</span>
        </div>
        <div className="genExp-amounts">
          <span>Balance Due</span><span>₹{summary?.balanceDue}</span>
        </div>
      </div>

      <div className="genExp-signature">
        <p>For {firmName}</p>
        <p>Authorized Signature</p>
      </div>
    </div>
  </div>
);

const GenExpFooter = ({ currentPage = 1, totalPages = 1 }) => (
  <footer className="genExp__stickyFooter">
    <p>Page {currentPage} of {totalPages}</p>
    <p>This is an automatically generated report. Powered by Transport360</p>
    <div style={{ display: 'flex', gap: '4px' }}>
      <GooglePlayIcon />
      <AppStoreIcon />
    </div>
  </footer>
);

// ─── Scale Hook (replaces the inline script) ─────────────────────────────────
const useScalePage = (containerRef) => {
  useEffect(() => {
    const designWidth = 794;

    const scale = () => {
      const screenWidth = window.innerWidth;
      const body = document.body;
      if (screenWidth <= 768) {
        const s = screenWidth / designWidth;
        body.style.transform = `scale(${s})`;
        if (containerRef.current) {
          body.style.height = `${containerRef.current.offsetHeight * s}px`;
        }
        body.style.overflow = 'auto';
      } else {
        body.style.transform = 'none';
        body.style.height = 'auto';
        body.style.overflow = 'visible';
      }
    };

    scale();
    window.addEventListener('resize', scale);
    return () => window.removeEventListener('resize', scale);
  }, [containerRef]);
};

// ─── Main Component ──────────────────────────────────────────────────────────

const GenExpense = ({ data = {} }) => {
  console.log(data)
  const containerRef = useRef(null);
  useScalePage(containerRef);

  const {
    invoiceNo,
    firm,
    vendor,
    meta,
    items,
    totals,
    amountInWords,
    termsConditions,
    summary,
    currentPage,
    totalPages,
  } = data;

  return (
    <div className="genExp-container" ref={containerRef}>
      <GenExpHeader invoiceNo={invoiceNo} />
      <GenExpTopInfo firm={firm} vendor={vendor} meta={meta} />
      <GenExpTable items={items} totals={totals} />
      <GenExpBottom
        amountInWords={amountInWords}
        termsConditions={termsConditions}
        summary={summary}
        firmName={firm?.name}
      />
      <GenExpFooter currentPage={currentPage} totalPages={totalPages} />
    </div>
  );
};

export default GenExpense;